// ============================================================================
// STEP 5: GEN CONFIG (Weighting Method)
// ============================================================================

export type WeightingStrategy =
  | 'equal'
  | 'price_weighted'
  | 'market_cap'
  | 'equal_buckets'
  | 'metric_weighting'
  | 'risk_budgeting'
  | 'clustered_risk_budgeting'
  | 'inverse_risk'
  | 'minimum_variance'
  | 'maximum_diversification'
  | 'sharpe_max'
  | 'target_return'
  | 'target_volatility'
  | 'tracking_error_min'
  | 'cvar'
  | 'drawdown'
  | 'factor_targeting'
  | 'neutral';

export interface MetricWeightingParams {
  metric_columns: Array<string>;
  metric_weight: Array<number>;
  metric_normalizations: Array<string>;
  metric_inverse_modes: Array<boolean>;
  weighting_combination: 'product' | 'sum';
  inverse_mode: boolean;
  fill_missing: number;
  target_long: number;
  target_short: number;
}

export interface GenConfig {
  weighting_strategy: WeightingStrategy;
  weighting_params: Record<string, unknown>;
}

export interface StrategyCategory {
  id: string;
  label: string;
  description: string;
  icon: string;
  models: Array<StrategyModel>;
}

export interface StrategyModel {
  value: WeightingStrategy;
  label: string;
  description: string;
}

export interface ModelDetail {
  formula: string;
  explanation: string;
  definitions: Array<{ symbol: string; description: string }>;
}

export interface AllocationItem {
  name: string;
  value: number;
  color: string;
}

export interface MetricDefinition {
  value: string;
  label: string;
  description: string;
}

export interface MetricCategory {
  name: string;
  metrics: Array<MetricDefinition>;
}

export interface SelectedMetric {
  column: string;
  label: string;
  weight: number;
  normalization: 'z_score' | 'min_max' | 'rank' | 'none';
  inverse: boolean;
}

export interface ExposureLimit {
  enabled: boolean;
  min: number | null;
  max: number;
}

export interface ConcentrationLimitsState {
  enabled: boolean;
  position: ExposureLimit;
  sector: ExposureLimit;
  country: ExposureLimit;
  enforceDuringOptimization: boolean;
}

export type SaleLotMethod = 'HIFO' | 'FIFO';

export interface TaxLossHarvestingState {
  enabled: boolean;
  // Optimizer params
  maxTrackingError: number; // UI: 1-5 (%), backend: 0.01-0.05
  longTermTaxRate: number; // UI: 0-50 (%), backend: 0.0-0.50
  shortTermTaxRate: number; // UI: 0-50 (%), backend: 0.0-0.50
  saleLotMethod: SaleLotMethod;
  // Executor params
  opportunisticHarvest: boolean;
  minLossPctToHarvest: number; // UI: 3-10 (%), backend: 0.03-0.10
  maxRealizedGainDollars: number;
  washSaleWindowDays: number;
  minHoldingDays: number;
  // Advanced
  maxNetRealizedGainDollars: number;
  taxPenaltyMultiplier: number;
}

/**
 * Optional transaction-cost assumption for the simulation (plan PR 17).
 * Off by default: the backtest is then gross of costs and the report says so.
 * When on, every reconstitution and rebalance is charged `oneWayBps` basis
 * points of the notional traded (buys and sells alike), the first book too
 * when `chargeInitialPositions`, and a net-of-costs NAV is shown beside the
 * gross one. Backend block: `transaction_costs` on the BacktestConfig.
 */
export interface TransactionCostsState {
  enabled: boolean;
  /** Commissions + half-spread + slippage, per unit of notional traded. */
  oneWayBps: number;
  /** Override for the short leg; null = same as oneWayBps. */
  shortOneWayBps: number | null;
  /** Charge the purchase of the first book on the seed day. */
  chargeInitialPositions: boolean;
}

export const DEFAULT_TRANSACTION_COSTS: TransactionCostsState = {
  enabled: false,
  oneWayBps: 10,
  shortOneWayBps: null,
  chargeInitialPositions: true,
};
